import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  FormControl,
  FormLabel,
  Input,
  Heading,
  SimpleGrid,
  Badge,
  useColorModeValue,
  Flex,
  IconButton,
  Alert,
  AlertIcon,
  useToast,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, EditIcon } from '@chakra-ui/icons';

interface MealDate {
  date: string;
  timeWindow: {
    start: string;
    end: string;
  };
  isClaimed?: boolean;
}

interface MealScheduleCustomizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (customDates: MealDate[]) => void;
  defaultDates: string[];
  defaultTimeWindow: {
    start: string;
    end: string;
  };
  currentCustomSchedule?: MealDate[] | null;
  claimedDates?: string[];
}

const MealScheduleCustomizationModal: React.FC<MealScheduleCustomizationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  defaultDates,
  defaultTimeWindow,
  currentCustomSchedule,
  claimedDates = [],
}) => {
  const [currentStep, setCurrentStep] = useState<'dates' | 'times'>('dates');
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [customMealDates, setCustomMealDates] = useState<MealDate[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [highlightedErrors, setHighlightedErrors] = useState<Record<number, boolean>>({});
  const [editingTimeIndex, setEditingTimeIndex] = useState<number | null>(null);
  const toast = useToast();

  const [bulkStartTime, setBulkStartTime] = useState<string>('');
  const [bulkEndTime, setBulkEndTime] = useState<string>('');

  // Initialize with current custom schedule or default dates when modal opens
  useEffect(() => {
    if (isOpen) {
      if (currentCustomSchedule && currentCustomSchedule.length > 0) {
        // Use existing custom schedule
        setSelectedDates(currentCustomSchedule.map(meal => meal.date));
        setCustomMealDates(currentCustomSchedule);
      } else {
        // Use default dates
        setSelectedDates(defaultDates);
        const initialMealDates = defaultDates.map(date => ({
          date,
          timeWindow: { ...defaultTimeWindow }
        }));
        setCustomMealDates(initialMealDates);
      }
      
      setCurrentStep('dates');
      setHighlightedErrors({}); // Clear any previous error highlights
    }
  }, [isOpen, defaultDates, defaultTimeWindow, currentCustomSchedule]);

  // Initialize bulk times only when first entering times step
  useEffect(() => {
    if (currentStep === 'times' && (!bulkStartTime || !bulkEndTime)) {
      setBulkStartTime(defaultTimeWindow.start);
      setBulkEndTime(defaultTimeWindow.end);
    }
  }, [currentStep, defaultTimeWindow, bulkStartTime, bulkEndTime]);

  // Format date for display
  const formatDate = (dateString: string): string => {
    const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
    const date = new Date(Date.UTC(year, month - 1, day));
    const dayOfWeek = date.getUTCDay();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return `${dayNames[dayOfWeek]} ${month}/${day}/${year.toString().slice(2, 4)}`;
  };

  // Format time for display
  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Generate calendar days for the current month
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isPast = dateStr < todayStr;
      days.push({ day, dateStr, isPast });
    }
    
    return days;
  };

  const handleApplyBulkTimes = () => {
    // Validate bulk time window first
    const error = validateTimeWindow(bulkStartTime, bulkEndTime);
    if (error) {
      toast({
        title: 'Invalid time window',
        description: error,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Apply to all unclaimed dates
    setCustomMealDates(prev => {
      return prev.map(mealDate => {
        // Only update unclaimed dates
        if (claimedDates.includes(mealDate.date)) {
          return mealDate;
        }
        return {
          ...mealDate,
          timeWindow: {
            start: bulkStartTime,
            end: bulkEndTime
          }
        };
      });
    });

    // Clear any error highlights
    setHighlightedErrors({});

    toast({
      title: 'Times updated',
      description: 'Bulk times applied to all available dates.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleDateToggle = (dateStr: string) => {
    // Don't allow toggling claimed dates
    if (claimedDates.includes(dateStr)) return;
    
    setSelectedDates(prev => {
      if (prev.includes(dateStr)) {
        return prev.filter(d => d !== dateStr);
      } else {
        return [...prev, dateStr].sort();
      }
    });
  };

  // Validate time windows
  const validateTimeWindow = (startTime: string, endTime: string): string | null => {
    if (!startTime || !endTime) return null;
    
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);
    
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    if (endTotalMinutes <= startTotalMinutes) {
      return 'End time must be after start time';
    }
    
    return null;
  };

  // Validate all time windows and return errors with details
  const validateAllTimeWindows = (mealDates: MealDate[]): { errors: Record<number, string>; hasErrors: boolean } => {
    const errors: Record<number, string> = {};
    
    mealDates.forEach((mealDate, index) => {
      const error = validateTimeWindow(mealDate.timeWindow.start, mealDate.timeWindow.end);
      if (error) {
        errors[index] = error;
      }
    });
    
    return { errors, hasErrors: Object.keys(errors).length > 0 };
  };

  const handleTimeChange = (dateIndex: number, field: 'start' | 'end', value: string) => {
    const mealDate = customMealDates[dateIndex];
    
    // Don't allow editing claimed dates
    if (claimedDates.includes(mealDate.date)) return;
    
    setCustomMealDates(prev => {
      const updated = [...prev];
      updated[dateIndex] = {
        ...updated[dateIndex],
        timeWindow: {
          ...updated[dateIndex].timeWindow,
          [field]: value
        }
      };
      return updated;
    });

    // Clear error highlight when user makes changes (no validation yet)
    if (highlightedErrors[dateIndex]) {
      setHighlightedErrors(prev => {
        const updated = { ...prev };
        delete updated[dateIndex];
        return updated;
      });
    }
  };

  const handleDoneEditing = (index: number) => {
    const mealDate = customMealDates[index];
    
    // Don't allow editing claimed dates
    if (claimedDates.includes(mealDate.date)) return;
    
    // Validate the time window for this specific day
    const error = validateTimeWindow(mealDate.timeWindow.start, mealDate.timeWindow.end);
    
    if (error) {
      // Show error and keep editing mode open
      setHighlightedErrors(prev => ({ ...prev, [index]: true }));
      toast({
        title: 'Invalid time window',
        description: `Day ${index + 1}: ${error}`,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    
    // Clear any errors and close editing mode
    setHighlightedErrors(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
    setEditingTimeIndex(null);
  };

  const handleNextStep = () => {
    if (currentStep === 'dates') {
      if (selectedDates.length === 0) {
        toast({
          title: 'No dates selected',
          description: 'Please select at least one meal date.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      // Prepare meal dates with times
      const mealDates = selectedDates.map(date => {
        const existing = customMealDates.find(md => md.date === date);
        return existing || {
          date,
          timeWindow: { ...defaultTimeWindow }
        };
      });
      setCustomMealDates(mealDates);
      setCurrentStep('times');
    } else {
      // Validate time windows before saving
      const validation = validateAllTimeWindows(customMealDates);
      
      if (validation.hasErrors) {
        // Highlight error fields
        const errorIndexes: Record<number, boolean> = {};
        Object.keys(validation.errors).forEach(index => {
          errorIndexes[parseInt(index)] = true;
        });
        setHighlightedErrors(errorIndexes);

        // Show toast with error details
        const errorCount = Object.keys(validation.errors).length;
        const errorDates = Object.keys(validation.errors).map(index => {
          const mealDate = customMealDates[parseInt(index)];
          return formatDate(mealDate.date);
        });

        toast({
          title: 'Invalid time windows',
          description: `Please fix the time windows for: ${errorDates.join(', ')}. End time must be after start time.`,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        return;
      }

      // Save if no errors
      onSave(customMealDates);
      onClose();
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const calendarDays = generateCalendarDays();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent maxW="600px">
        <ModalHeader>
          {currentStep === 'dates' ? 'Select Meal Dates' : 'Set Drop Off Times'}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          {currentStep === 'dates' ? (
            <VStack spacing={4} align="stretch">
              
              {/* Calendar Header */}
              <HStack justify="space-between" align="center">
                <IconButton
                  icon={<ChevronLeftIcon />}
                  onClick={handlePrevMonth}
                  aria-label="Previous month"
                  size="sm"
                />
                <Heading size="md">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </Heading>
                <IconButton
                  icon={<ChevronRightIcon />}
                  onClick={handleNextMonth}
                  aria-label="Next month"
                  size="sm"
                />
              </HStack>

              {/* Calendar Grid */}
              <Box>
                {/* Day headers */}
                <SimpleGrid columns={7} spacing={1} mb={2}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <Text key={day} textAlign="center" fontSize="sm" fontWeight="bold" color="gray.500">
                      {day}
                    </Text>
                  ))}
                </SimpleGrid>
                
                {/* Calendar days */}
                <SimpleGrid columns={7} spacing={1}>
                  {calendarDays.map((dayData, index) => {
                    if (!dayData) {
                      return <Box key={index} height="40px" />;
                    }
                    
                    const isClaimed = claimedDates.includes(dayData.dateStr);
                    const isSelected = selectedDates.includes(dayData.dateStr);
                    
                    return (
                      <Button
                        key={index}
                        size="sm"
                        height="40px"
                        width="100%"
                        variant={isSelected ? "solid" : "outline"}
                        colorScheme={
                          isClaimed ? "gray" : 
                          isSelected ? "green" : "gray"
                        }
                        isDisabled={dayData.isPast || isClaimed}
                        onClick={() => handleDateToggle(dayData.dateStr)}
                        fontSize="xs"
                        bg={isClaimed ? "gray.500" : undefined}
                        _hover={isClaimed ? { bg: "gray.200" } : undefined}
                        cursor={isClaimed ? "not-allowed" : "pointer"}
                        position="relative"
                      >
                        {dayData.day}
                        {isClaimed && (
                          <Badge
                            position="absolute"
                            top="-2px"
                            right="-2px"
                            size="xs"
                            colorScheme="red"
                            fontSize="6px"
                            borderRadius="full"
                          >
                            •
                          </Badge>
                        )}
                      </Button>
                    );
                  })}
                </SimpleGrid>
              </Box>

              {/* Selected dates summary */}
              {selectedDates.length > 0 && (
                <Box p={3} bg="blue.50" borderRadius="md">
                  <Text fontWeight="bold" mb={2}>Selected Dates ({selectedDates.length}):</Text>
                  <VStack align="start" spacing={1}>
                    {selectedDates.map((date, index) => (
                      <Text key={date} fontSize="sm">
                        Day {index + 1}: {formatDate(date)} {claimedDates.includes(date) ? '(Someone signed up)' : ''}
                      </Text>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          ) : (
            <VStack spacing={3} align="stretch">
               <Box p={4} borderWidth="2px" borderRadius="md" borderColor="green.200">
                <Text fontWeight="bold" mb={3} color="green.700">
                  Set Times for All Dates
                </Text>
                <HStack spacing={4} mb={3}>
                  <FormControl flex={1}>
                    <FormLabel fontSize="sm">Start Time</FormLabel>
                    <Input
                      type="time"
                      value={bulkStartTime}
                      onChange={(e) => setBulkStartTime(e.target.value)}
                      bg="white"
                    />
                  </FormControl>
                  
                  <FormControl flex={1}>
                    <FormLabel fontSize="sm">End Time</FormLabel>
                    <Input
                      type="time"
                      value={bulkEndTime}
                      onChange={(e) => setBulkEndTime(e.target.value)}
                      bg="white"
                    />
                  </FormControl>
                </HStack>
                
                <HStack spacing={3} justify="right">
                  <Button 
                    size="sm" 
                    colorScheme="green"
                    onClick={handleApplyBulkTimes}
                  >
                    Apply to All Dates
                  </Button>
                  <Text fontSize="sm" color="gray.600" paddingRight={{ base: 0, lg: 9 }}>
                    {formatTime(bulkStartTime)} - {formatTime(bulkEndTime)}
                  </Text>
                </HStack>
              </Box>
              
              {customMealDates.length === 0 && (
                <Alert status="warning">
                  <AlertIcon />
                  No dates selected. Please go back and select at least one date.
                </Alert>
              )}
              
              {customMealDates.map((mealDate, index) => {
                const isClaimed = claimedDates.includes(mealDate.date);
                
                return (
                  <Box key={mealDate.date}>
                    {editingTimeIndex === index && !isClaimed ? (
                      // Edit mode - full expanded view (only for unclaimed dates)
                      <Box p={4} borderWidth="1px" borderRadius="md" bg="blue.50">
                        <Text fontWeight="bold" mb={3}>
                          Day {index + 1}: {formatDate(mealDate.date)}
                        </Text>
                        
                        <HStack spacing={4} mb={3}>
                          <FormControl flex={1} isInvalid={highlightedErrors[index]}>
                            <FormLabel fontSize="sm">Start Time</FormLabel>
                            <Input
                              type="time"
                              value={mealDate.timeWindow.start}
                              onChange={(e) => handleTimeChange(index, 'start', e.target.value)}
                            />
                          </FormControl>
                          
                          <FormControl flex={1} isInvalid={highlightedErrors[index]}>
                            <FormLabel fontSize="sm">End Time</FormLabel>
                            <Input
                              type="time"
                              value={mealDate.timeWindow.end}
                              onChange={(e) => handleTimeChange(index, 'end', e.target.value)}
                            />
                          </FormControl>
                        </HStack>
                        
                        <HStack spacing={2}>
                          <Button 
                            size="sm" 
                            colorScheme="blue"
                            onClick={() => handleDoneEditing(index)}
                          >
                            Done
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              // Cancel - clear any errors and close without saving
                              setHighlightedErrors(prev => {
                                const updated = { ...prev };
                                delete updated[index];
                                return updated;
                              });
                              setEditingTimeIndex(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </HStack>
                      </Box>
                    ) : (
                      // Compact one-row view
                      <HStack 
                        p={3} 
                        borderWidth="1px" 
                        borderRadius="md" 
                        bg={isClaimed ? "gray.100" : "white"}
                        justify="space-between"
                        opacity={isClaimed ? 0.7 : 1}
                      >
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="medium">
                            Day {index + 1}: {formatDate(mealDate.date)}
                          </Text>
                          {isClaimed && (
                            <Text fontSize="xs" color="">
                              Someone has signed up
                            </Text>
                          )}
                        </VStack>
                        
                        <HStack spacing={3}>
                          <Text fontSize="sm" color={highlightedErrors[index] ? "red.500" : "gray.600"}>
                            {formatTime(mealDate.timeWindow.start)} - {formatTime(mealDate.timeWindow.end)}
                          </Text>
                          
                          {!isClaimed && (
                            <IconButton
                              icon={<EditIcon />}
                              size="sm"
                              variant="outline"
                              colorScheme="blue"
                              aria-label="Edit time"
                              onClick={() => setEditingTimeIndex(index)}
                            />
                          )}
                        </HStack>
                      </HStack>
                    )}
                  </Box>
                );
              })}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <VStack spacing={3} width="100%" align="stretch">
            {/* Top row buttons - Back and Cancel */}
            <HStack 
              spacing={3} 
              justify="center"
              display={{ base: currentStep === 'times' ? 'flex' : 'none', md: 'none' }}
            >
              {currentStep === 'times' && (
                <Button variant="outline" onClick={() => setCurrentStep('dates')} flex={1}>
                  Back to Dates
                </Button>
              )}
              <Button variant="outline" onClick={onClose} flex={1}>
                Cancel
              </Button>
            </HStack>
            
            {/* Desktop layout - all buttons in one row */}
            <HStack 
              spacing={3} 
              justify="center"
              display={{ base: 'none', md: 'flex' }}
            >
              {currentStep === 'times' && (
                <Button variant="outline" onClick={() => setCurrentStep('dates')}>
                  Back to Dates
                </Button>
              )}
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleNextStep}
                isDisabled={currentStep != 'dates' && editingTimeIndex !== null} 
                >
                {currentStep === 'dates' ? 'Next: Set Times' : 'Save Schedule'}
              </Button>
            </HStack>
            
            {/* Mobile layout - Save button on bottom */}
            <Button
              colorScheme="blue"
              onClick={handleNextStep}
              width="100%"
              display={{ base: 'block', md: 'none' }}
              isDisabled={currentStep != 'dates' && editingTimeIndex !== null} 
            >
              {currentStep === 'dates' ? 'Next: Set Times' : 'Save Schedule'}
            </Button>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MealScheduleCustomizationModal;